import { useState, useEffect, useCallback, useMemo } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import axiosInstance from "../../../Config/axiosInstance";
import AddClientContacts from "./AddClientContacts";
import "./AddClientDialog.css";

const initialErrors = {
  clientCode: "",
  cui: "",
  name: "",
  address: "",
  phone: "",
  status: "",
  responsiblePerson: "",
  submit: "",
};

const EditClient = ({ isOpen, onClose, client, fetchClients }) => {
  const [allContacts, setAllContacts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    clientCode: "",
    cui: "",
    clientName: "",
    address: "",
    phone: "",
    status: "",
    responsiblePerson: "",
    contacts: [],
  });
  const [errors, setErrors] = useState(initialErrors);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Componente animate pentru react-select
  const animatedComponents = useMemo(() => makeAnimated(), []);

  // Lista de opțiuni pentru statusul clientului.
  const statusOptions = useMemo(
    () => [
      { value: "Active", label: "Active" },
      { value: "Inactive", label: "Inactive" },
      { value: "Prospect", label: "Prospect" },
      { value: "Blocked", label: "Blocked" },
    ],
    []
  );

  // Transformă lista de angajați în opțiuni pentru react-select.
  const employeeOptions = useMemo(
    () =>
      employees.map((employee) => ({
        value: employee.id,
        label: `${employee.first_name} ${employee.last_name}`,
      })),
    [employees]
  );

  // Caută în lista de opțiuni elementul corespunzător unei valori salvate în state.
  const findOption = useCallback(
    (options, value) =>
      options.find((option) => String(option.value) === String(value)) || null,
    []
  );

  // Blochează controalele formularului cât timp update-ul este în curs.
  const disabledAll = useMemo(() => isSubmitting, [isSubmitting]);

  // Stiluri custom pentru componentele Select.
  // Sunt memorate pentru performanță și consistență vizuală.
  const selectMenuStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 20000 }),
      menu: (base) => ({ ...base, zIndex: 20000 }),
      control: (base, state) => ({
        ...base,
        minHeight: "40px",
        borderRadius: "12px",
        borderWidth: "1.5px",
        borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
        boxShadow: state.isFocused
          ? "0 0 0 4px rgba(59, 130, 246, 0.1), 0 4px 8px -4px rgba(59, 130, 246, 0.3)"
          : "0 2px 4px rgba(0, 0, 0, 0.02)",
        "&:hover": {
          borderColor: "#3b82f6",
        },
      }),
      placeholder: (base) => ({
        ...base,
        color: "#94a3b8",
      }),
    }),
    []
  );

  // Formatează mesajele astfel încât să înceapă cu literă mare.
  const capitalizeFirstLetter = useCallback((text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  }, []);

  // Extrage într-un mod generic mesajul de eroare venit din backend.
  const extractErrorMessage = useCallback((error) => {
    const data = error?.response?.data;

    if (typeof data === "string") return data;
    if (data?.detail) return data.detail;
    if (data?.message) return data.message;

    if (data && typeof data === "object") {
      const firstKey = Object.keys(data)[0];
      if (firstKey) {
        const value = data[firstKey];
        if (Array.isArray(value)) return value[0];
        if (typeof value === "string") return value;
      }
    }

    return error?.message || "An unexpected error occurred";
  }, []);

  // Încarcă angajații pentru câmpul Responsible Person.
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/employees/");
      setEmployees(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    }
  }, []);

  // Încarcă toate contactele clientului selectat și pregătește datele pentru formular.
  // În plus:
  // - convertește contactele într-un format compatibil cu react-select
  // - selectează implicit doar contactele active
  // - completează formularul cu valorile clientului editat
  const fetchClientContacts = useCallback(async () => {
    if (!client?.id) return;

    try {
      const contactsResponse = await axiosInstance.get(`/client-contacts/`);

      const clientContacts = contactsResponse.data.filter((contact) => {
        const contactClientId =
          contact.client_id ||
          contact.client ||
          (contact.client && contact.client.id) ||
          (contact.client &&
            typeof contact.client === "number" &&
            contact.client);

        return String(contactClientId) === String(client.id);
      });

      const allContactsData = clientContacts.map((contact) => ({
        value: contact.id,
        label: `${contact.name} - ${contact.position}`,
        status: contact.contact_status || "Active",
        client: client.id,
        originalData: contact,
      }));

      const activeContacts = allContactsData.filter(
        (contact) => contact.status === "Active"
      );

      setAllContacts(allContactsData);
      setFormData({
        clientCode: client.client_code ? client.client_code.slice(-3) : "",
        cui: client.cui || "",
        clientName: client.name || "",
        address: client.address || "",
        phone: client.phone || "",
        status: client.status || "",
        responsiblePerson:
          client.responsible_person?.id ||
          client.responsible_person ||
          "",
        contacts: activeContacts,
      });
      setErrors(initialErrors);
      setSubmitted(false);
    } catch (error) {
      console.error("Error fetching client contacts:", error);

      setAllContacts([]);
      setFormData({
        clientCode: client.client_code ? client.client_code.slice(-3) : "",
        cui: client.cui || "",
        clientName: client.name || "",
        address: client.address || "",
        phone: client.phone || "",
        status: client.status || "",
        responsiblePerson:
          client.responsible_person?.id ||
          client.responsible_person ||
          "",
        contacts: [],
      });
    }
  }, [client]);

  // La deschiderea dialogului de editare, dacă există client selectat
  // și nu este deschis formularul secundar de contact, se încarcă datele clientului.
  useEffect(() => {
    if (client && isOpen && !showContactForm) {
      fetchEmployees();
      fetchClientContacts();
    }
  }, [client, isOpen, showContactForm, fetchEmployees, fetchClientContacts]);

  // Închide dialogul principal și resetează stările auxiliare.
  const handleClose = useCallback(() => {
    if (disabledAll) return;
    setShowContactForm(false);
    setErrors(initialErrors);
    setSubmitted(false);
    onClose?.();
  }, [disabledAll, onClose]);

  // Validează formularul de editare:
  // - verifică toate câmpurile obligatorii
  // - verifică lungimea codului client
  // - actualizează obiectul errors pentru afișare în UI
  const validateForm = useCallback(() => {
    const nextErrors = { ...initialErrors };

    if (!formData.clientCode.trim()) {
      nextErrors.clientCode = "Client code is required";
    } else if (formData.clientCode.trim().length !== 3) {
      nextErrors.clientCode = "Client code must be exactly 3 characters";
    }

    if (!formData.cui.trim()) {
      nextErrors.cui = "CUI is required";
    }

    if (!formData.clientName.trim()) {
      nextErrors.name = "Client name is required";
    }

    if (!formData.address.trim()) {
      nextErrors.address = "Address is required";
    }

    if (!formData.phone.trim()) {
      nextErrors.phone = "Phone is required";
    }

    if (!formData.status) {
      nextErrors.status = "Status is required";
    }

    if (!formData.responsiblePerson) {
      nextErrors.responsiblePerson = "Responsible person is required";
    }

    setErrors((prev) => ({
      ...prev,
      ...nextErrors,
      submit: "",
    }));

    return (
      !nextErrors.clientCode &&
      !nextErrors.cui &&
      !nextErrors.name &&
      !nextErrors.address &&
      !nextErrors.phone &&
      !nextErrors.status &&
      !nextErrors.responsiblePerson
    );
  }, [formData]);

  // Gestionează modificarea inputurilor simple.
  // Pentru clientCode aplică reguli speciale:
  // - doar litere
  // - uppercase
  // - maxim 3 caractere
  // Tot aici se șterg erorile pentru câmpul modificat.
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    if (name === "clientCode") {
      const uppercaseValue = value
        .toUpperCase()
        .replace(/[^A-Z]/g, "")
        .slice(0, 3);

      setFormData((prev) => ({ ...prev, [name]: uppercaseValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    setErrors((prev) => ({
      ...prev,
      [name === "clientName" ? "name" : name]: "",
      submit: "",
    }));
  }, []);

  // Gestionează schimbarea statusului din react-select
  // și curăță eroarea aferentă câmpului.
  const handleStatusChange = useCallback((selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      status: selectedOption ? selectedOption.value : "",
    }));

    setErrors((prev) => ({
      ...prev,
      status: "",
      submit: "",
    }));
  }, []);

  // Gestionează schimbarea persoanei responsabile din react-select
  // și curăță eroarea aferentă câmpului.
  const handleResponsiblePersonChange = useCallback((selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      responsiblePerson: selectedOption ? selectedOption.value : "",
    }));

    setErrors((prev) => ({
      ...prev,
      responsiblePerson: "",
      submit: "",
    }));
  }, []);

  // Gestionează actualizarea listei de contacte asociate clientului.
  // Când un contact este scos din selecție, este marcat Inactive în backend.
  // Când un contact inactiv este selectat din nou, este reactivat.
  // La final, se actualizează atât lista selectată, cât și lista completă locală.
  const handleChangeContacts = useCallback(
    async (selectedContacts) => {
      const selected = selectedContacts || [];

      const removedContacts = formData.contacts.filter(
        (contact) =>
          !selected.some((selectedItem) => selectedItem.value === contact.value)
      );

      await Promise.all(
        removedContacts.map(async (contact) => {
          try {
            await axiosInstance.patch(`/client-contacts/${contact.value}/`, {
              contact_status: "Inactive",
            });
          } catch (error) {
            console.error(
              `Error updating contact status for ${contact.label}:`,
              error
            );
          }
        })
      );

      await Promise.all(
        selected.map(async (contact) => {
          const existingContact = allContacts.find(
            (c) => c.value === contact.value
          );

          if (existingContact && existingContact.status === "Inactive") {
            try {
              await axiosInstance.patch(`/client-contacts/${contact.value}/`, {
                contact_status: "Active",
              });
            } catch (error) {
              console.error(`Error activating contact ${contact.label}:`, error);
            }
          }
        })
      );

      setFormData((prev) => ({ ...prev, contacts: selected }));

      const updatedAllContacts = allContacts.map((contact) => {
        const isSelected = selected.some(
          (selectedItem) => selectedItem.value === contact.value
        );

        return {
          ...contact,
          status: isSelected ? "Active" : "Inactive",
        };
      });

      setAllContacts(updatedAllContacts);
    },
    [formData.contacts, allContacts]
  );

  // Adaugă un contact nou direct din dialogul secundar.
  // După salvare:
  // - contactul este creat în backend
  // - este adăugat în lista locală
  // - este selectat automat în formular
  // - formularul secundar se închide
  // - lista completă de contacte se reîncarcă pentru sincronizare
  const handleAddContactToList = useCallback(
    async (newContact) => {
      try {
        const contactWithClient = {
          ...newContact,
          client: client.id,
          contact_status: "Active",
        };

        const contactResponse = await axiosInstance.post(
          "/client-contacts/",
          contactWithClient
        );

        const newContactObj = {
          value: contactResponse.data.id,
          label: `${contactResponse.data.name} - ${contactResponse.data.position}`,
          status: "Active",
          client: client.id,
          originalData: contactResponse.data,
        };

        setAllContacts((prev) => [...prev, newContactObj]);

        setFormData((prev) => ({
          ...prev,
          contacts: [...prev.contacts, newContactObj],
        }));

        setShowContactForm(false);
        await fetchClientContacts();
      } catch (error) {
        console.error("Error saving contact:", error);
        setErrors((prev) => ({
          ...prev,
          submit:
            error?.response?.data?.message ||
            error?.response?.data?.detail ||
            error?.message ||
            "Error saving contact",
        }));
      }
    },
    [client, fetchClientContacts]
  );

  // Deschide formularul secundar pentru adăugarea unui contact nou.
  const handleShowContactForm = useCallback(() => {
    if (disabledAll) return;
    setShowContactForm(true);
  }, [disabledAll]);

  // Revine din formularul de contact în formularul principal de editare client.
  const handleBackToClientForm = useCallback(() => {
    if (disabledAll) return;
    setShowContactForm(false);
  }, [disabledAll]);

  // Funcția principală de submit pentru editarea clientului:
  // 1. validează formularul
  // 2. construiește obiectul trimis spre backend
  // 3. face update clientului prin API
  // 4. reîncarcă lista din părinte
  // 5. închide dialogul la succes
  // În caz de eroare, mapează mesajele atât pe câmpuri cât și pe submit general.
  const handleUpdateClient = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitted(true);
      setErrors(initialErrors);

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      const updatedClient = {
        name: formData.clientName,
        address: formData.address,
        phone: formData.phone,
        status: formData.status,
        cui: formData.cui,
        code_suffix: formData.clientCode,
        responsible_person: formData.responsiblePerson,
      };

      try {
        const response = await axiosInstance.put(
          `/clients/${client.id}/`,
          updatedClient
        );

        if (response.status === 200) {
          await fetchClients?.();
          handleClose();
        }
      } catch (error) {
        console.error("Error updating client:", error);
        console.error("Error response:", error.response?.data);

        const responseData = error?.response?.data;

        if (responseData && typeof responseData === "object") {
          setErrors((prev) => ({
            ...prev,
            clientCode: responseData.code_suffix?.[0] || "",
            cui: responseData.cui?.[0] || "",
            name: responseData.name?.[0] || "",
            address: responseData.address?.[0] || "",
            phone: responseData.phone?.[0] || "",
            status: responseData.status?.[0] || "",
            responsiblePerson: responseData.responsible_person?.[0] || "",
            submit: extractErrorMessage(error),
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            submit: extractErrorMessage(error),
          }));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateForm,
      formData,
      client,
      fetchClients,
      handleClose,
      extractErrorMessage,
    ]
  );

  // Dacă dialogul nu este deschis sau clientul nu există, nu se afișează nimic.
  if (!isOpen || !client) return null;

  // Variabile ajutătoare pentru afișarea erorilor doar după primul submit.
  const showClientCodeError = submitted && !!errors.clientCode;
  const showCuiError = submitted && !!errors.cui;
  const showNameError = submitted && !!errors.name;
  const showAddressError = submitted && !!errors.address;
  const showPhoneError = submitted && !!errors.phone;
  const showStatusError = submitted && !!errors.status;
  const showResponsiblePersonError = submitted && !!errors.responsiblePerson;

  return (
    <>
      <Dialog
        open={isOpen && !showContactForm}
        onClose={disabledAll ? undefined : handleClose}
        maxWidth="sm"
        fullWidth
        className="add-client-dialog"
      >
        <DialogTitle className="acd-title">
          <span className="acd-header-title">Edit Client</span>
        </DialogTitle>

        <DialogContent dividers className="acd-content">
          <form onSubmit={handleUpdateClient} className="acd-form">
            {/* Secțiunea pentru codul clientului */}
            <div className="acd-form-group">
              <label className="acd-label">
                Client Code <span style={{ color: "red" }}>*</span>
                <small className="acd-label-hint">(3 characters)</small>
              </label>
              <input
                type="text"
                name="clientCode"
                value={formData.clientCode}
                onChange={handleChange}
                maxLength={3}
                disabled
                className={`acd-input ${
                  showClientCodeError ? "acd-input-error" : ""
                }`}
                placeholder="ABC"
                style={{ textTransform: "uppercase" }}
              />
              {showClientCodeError && (
                <div className="acd-field-error">{errors.clientCode}</div>
              )}
            </div>

            {/* Secțiunea pentru CUI */}
            <div className="acd-form-group">
              <label className="acd-label">
                CUI <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="cui"
                value={formData.cui}
                onChange={handleChange}
                disabled={disabledAll}
                className={`acd-input ${showCuiError ? "acd-input-error" : ""}`}
                placeholder="Enter CUI"
              />
              {showCuiError && (
                <div className="acd-field-error">
                  {capitalizeFirstLetter(errors.cui)}
                </div>
              )}
            </div>

            {/* Secțiunea pentru numele clientului */}
            <div className="acd-form-group">
              <label className="acd-label">
                Client Name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                disabled={disabledAll}
                className={`acd-input ${showNameError ? "acd-input-error" : ""}`}
                placeholder="Enter client name"
              />
              {showNameError && (
                <div className="acd-field-error">{errors.name}</div>
              )}
            </div>

            {/* Secțiunea pentru adresă */}
            <div className="acd-form-group">
              <label className="acd-label">
                Address <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                disabled={disabledAll}
                className={`acd-input ${
                  showAddressError ? "acd-input-error" : ""
                }`}
                placeholder="Enter address"
              />
              {showAddressError && (
                <div className="acd-field-error">{errors.address}</div>
              )}
            </div>

            {/* Secțiunea pentru telefon */}
            <div className="acd-form-group">
              <label className="acd-label">
                Phone <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={disabledAll}
                className={`acd-input ${
                  showPhoneError ? "acd-input-error" : ""
                }`}
                placeholder="Enter phone number"
              />
              {showPhoneError && (
                <div className="acd-field-error">{errors.phone}</div>
              )}
            </div>

            {/* Secțiunea pentru status */}
            <div className="acd-form-group">
              <label className="acd-label">
                Status <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                name="status"
                options={statusOptions}
                value={findOption(statusOptions, formData.status)}
                onChange={handleStatusChange}
                classNamePrefix="acd-react-select"
                placeholder="Select Status"
                isClearable
                isDisabled={disabledAll}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectMenuStyles}
                className={showStatusError ? "acd-select-error" : ""}
              />
              {showStatusError && (
                <div className="acd-field-error">{errors.status}</div>
              )}
            </div>

            {/* Secțiunea pentru persoana responsabilă */}
            <div className="acd-form-group">
              <label className="acd-label">
                Responsible Person <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                name="responsiblePerson"
                options={employeeOptions}
                value={findOption(employeeOptions, formData.responsiblePerson)}
                onChange={handleResponsiblePersonChange}
                classNamePrefix="acd-react-select"
                placeholder="Select Responsible Person"
                isClearable
                isSearchable
                isDisabled={disabledAll}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectMenuStyles}
                className={showResponsiblePersonError ? "acd-select-error" : ""}
              />
              {showResponsiblePersonError && (
                <div className="acd-field-error">
                  {errors.responsiblePerson}
                </div>
              )}
            </div>

            {/* Secțiunea pentru contactele asociate clientului */}
            <div className="acd-form-group">
              <label className="acd-label">Contact Persons</label>

              <div className="acd-contact-row">
                <div className="acd-contact-select">
                  <Select
                    options={allContacts.map((contact) => ({
                      value: contact.value,
                      label: contact.label,
                      status: contact.status,
                    }))}
                    value={formData.contacts}
                    isMulti
                    components={animatedComponents}
                    classNamePrefix="acd-react-select"
                    isDisabled={allContacts.length === 0 || isSubmitting}
                    placeholder={
                      allContacts.length === 0
                        ? "Add Contact"
                        : "Select contacts..."
                    }
                    noOptionsMessage={() =>
                      "No contacts available - add a contact first"
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      ...selectMenuStyles,
                      control: (base, state) => ({
                        ...selectMenuStyles.control(base, state),
                        backgroundColor:
                          allContacts.length === 0 || isSubmitting
                            ? "#f5f5f5"
                            : "#ffffff",
                        borderColor:
                          allContacts.length === 0 || isSubmitting
                            ? "#e0e0e0"
                            : state.isFocused
                            ? "#3b82f6"
                            : "#e2e8f0",
                        cursor:
                          allContacts.length === 0 || isSubmitting
                            ? "not-allowed"
                            : "default",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color:
                          allContacts.length === 0 || isSubmitting
                            ? "#9e9e9e"
                            : "#94a3b8",
                      }),
                    }}
                    onChange={handleChangeContacts}
                  />
                </div>

                <Button
                  type="button"
                  className="acd-btn acd-btn-add-contact"
                  onClick={handleShowContactForm}
                  disabled={isSubmitting}
                >
                  Add Contact
                </Button>
              </div>
            </div>

            {/* Mesaj general de eroare pentru submit */}
            {errors.submit && (
              <div className="acd-submit-error">{errors.submit}</div>
            )}
          </form>
        </DialogContent>

        <DialogActions className="acd-actions">
          {/* Buton de închidere */}
          <Button
            type="button"
            onClick={handleClose}
            className="acd-btn acd-btn-cancel"
            disabled={isSubmitting}
          >
            Close
          </Button>

          {/* Buton principal pentru actualizarea clientului */}
          <Button
            type="submit"
            onClick={handleUpdateClient}
            variant="contained"
            className="acd-btn acd-btn-create"
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={18} className="acd-spinner" />
              ) : null
            }
          >
            {isSubmitting ? "Updating..." : "Update Client"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog secundar pentru adăugarea unui contact nou */}
      <AddClientContacts
        isOpen={isOpen && showContactForm}
        onClose={handleBackToClientForm}
        onAddContact={handleAddContactToList}
        clientId={client.id}
      />
    </>
  );
};

export default EditClient;